/*
 * Http Errors Interceptor
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  Injectable,
  NgZone,
} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  Observable,
  throwError,
} from 'rxjs';
import { tap } from 'rxjs/operators';
import { NotificationService } from 'app/shared/services/notification.service';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import {
  AuthService,
  ClientService,
  FieldValidationErrorService,
  UserService,
} from 'app/shared/services';
import { ErrorsFormatterUtilService } from '../services/errors-formatter-util.service';
import { getReturnUriQueryParams } from 'app/shared/lib';
import { Constants } from 'app/shared/config/constants';
import { UiConfigService } from 'app/shared/services/ui-config.service';

@Injectable ()

export class HttpErrorInterceptor implements HttpInterceptor {

  constructor (
    private notificationService: NotificationService,
    private errorFormatterService: ErrorsFormatterUtilService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private zone: NgZone,
    private userService: UserService,
    private clientService: ClientService,
    private uiConfigService: UiConfigService,
    private fieldValidationErrorService: FieldValidationErrorService,
  ) {
  }

  intercept ( request: HttpRequest<any>, next: HttpHandler ): Observable<HttpEvent<any>> {
    return next.handle ( request )
      .pipe ( tap (
        ( a ) => {
          //
        },
        ( err: any ) => {
          if ( err instanceof HttpErrorResponse ) {
            const isReassignReferenceNumberError = err.status === 400 && err.error.errorCode === 'INVALID_RESOURCE_STATE';
            const isLogOutUrl = request.url.match ( /auth\/logout/g );
            const isLoginUrl = request.url.match ( /auth\/login/g );
            const isFieldValidationError = err.status === 400 && err.error.fieldValidationErrors;
            const isVerificationCodeError = request.url.match ( /verification-code\/validate/g );
            const isMfaExchangeError = request.url.match ( /auth\/mfa\/exchange/g );
            const isAiTokenError = request.url.match ( /auth\/conversational-ai/g );

            if ( err.status !== 401 && err.status !== 404 && !isReassignReferenceNumberError && !isLogOutUrl && !isFieldValidationError ) {
              const clientFormatterError = this.errorFormatterService.formatError ( err );

              this.notificationService.addNotification (
                'error', {
                  title: 'A HTTP error occurred',
                  ...clientFormatterError,
                } );
              return throwError ( err );
            } else if ( err.status === 404 || isReassignReferenceNumberError || isVerificationCodeError || isMfaExchangeError || isAiTokenError || isLogOutUrl || isLoginUrl ) {
              return throwError ( err );
            } else if ( isFieldValidationError ) {
              const clientFormatterError = this.errorFormatterService.formatError ( err );
              this.notificationService.addNotification (
                'error', {
                  title: err.error.fieldValidationErrors.length + ' answers are not valid',
                  ...clientFormatterError,
                },
              );
              for ( const fieldValidationError of err.error.fieldValidationErrors ) {
                this.fieldValidationErrorService.broadcastError ( fieldValidationError );
              }
              return throwError ( err );
            } else if ( err.status === 401 ) {
              this.logout ();
            }
            return throwError ( err );
          }
        },
      ) );
  }

  private logout () {
    const queryParams = getReturnUriQueryParams ( this.route.snapshot );

    const qParams = this.route.snapshot.queryParams;
    const path = location.pathname;

    const isRestorablePath = !path.match ( /login|404|recover|mfa\/callback/g ) && path !== '/';

    try {
      if ( isRestorablePath && this.userService.currentUser.userId ) {
        localStorage.setItem ( Constants.localStorageKeys.___gbru, btoa ( JSON.stringify ( {
          path: path.split ( '/' ),
          qParams,
          user: this.userService.currentUser.userId,
          [ Constants.localStorageKeys.reporterUser ]: this.userService.currentUser,
          [ Constants.localStorageKeys.reporterCurrentClient ]: this.userService.currentClient,
          [ Constants.localStorageKeys.reporterBusinessUnitsSettings ]: this.clientService.buSettings,
        } ) ) );
      } else if ( isRestorablePath && localStorage.getItem ( Constants.localStorageKeys.___gbru ) ) {
        const existingSnapshot = JSON.parse ( atob ( localStorage.getItem ( Constants.localStorageKeys.___gbru ) ) );
        if ( existingSnapshot?.path?.join ( '/' ) !== path ) {
          localStorage.removeItem ( Constants.localStorageKeys.___gbru );
        }
      }
    } catch ( e ) {
      console.error ( e );
    }

    const urlSegmentKey = this.uiConfigService.urlSegmentKey ? `/${ this.uiConfigService.urlSegmentKey }` : '';
    this.authService.logout ().subscribe ( { error: () => undefined } );
    this.zone.run ( () => {
      this.router.navigate ( [ `${ urlSegmentKey }/login` ], {
        queryParams,
        replaceUrl: false,
      } );
    } );
  }
}
